import { useState, useEffect, useRef } from "react";

interface StreamingAnimationOptions {
  charsPerInterval?: number;
  intervalMs?: number;
}

/**
 * Custom hook for character-by-character streaming animation
 * @param content - The full content to stream
 * @param options - Animation speed options
 * @returns The progressively displayed text
 */
export const useStreamingAnimation = (
  content: string,
  options: StreamingAnimationOptions = {}
): string => {
  const { charsPerInterval = 8, intervalMs = 10 } = options;
  const [displayedText, setDisplayedText] = useState("");
  const queueRef = useRef<string>("");
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Update queue when content changes
    queueRef.current = content;

    if (!queueRef.current || queueRef.current === displayedText) {
      return;
    }

    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Character-by-character streaming effect
    intervalRef.current = setInterval(() => {
      setDisplayedText((prev) => {
        const target = queueRef.current;
        if (prev.length >= target.length) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          return target;
        }

        // Add multiple characters at once for smoother streaming
        const charsToAdd = Math.min(charsPerInterval, target.length - prev.length);
        return target.slice(0, prev.length + charsToAdd);
      });
    }, intervalMs);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [content, charsPerInterval, intervalMs, displayedText]);

  return displayedText;
};
